import { useCallback, useState, useSyncExternalStore } from "react";
import { notifyManager, useQueryClient } from "@tanstack/react-query";
import { RUN_IN_FLIGHT_LINE } from "../lib/runNotify";

// isRunInFlightRefusal: a tree act the server refused under a live run lease
// (0159 F1): the thrown error carries the error envelope's code
// (usePatchProductTitle's ProductTitleError, the amend's AmendRefusedError in
// hooks/useAmendDecision.ts) and the code ends in `.run_in_flight`. Read by
// the code, never by the message.
export function isRunInFlightRefusal(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && code.endsWith(".run_in_flight");
}

// How a surface uses the rule (both optional).
export type RunInFlightRefusalOptions = {
  // The surface's ruled line for its refusal: F1's RUN_IN_FLIGHT_LINE unless
  // the surface has its own (the amend's, 0148 A34, from lib/reviewLines.ts's
  // amendRefusalLine).
  line?: string;
  // Whether the surface shows its own in-flight signal while runInFlight is
  // true (default true): the title editor's hidden pencil, the spec drawer's
  // held Amend. The covered-by amend has none - its refusal is its only in-flight
  // signal (0148 A35) - and passes false.
  ownInFlightSignal?: boolean;
};

// What a surface that shows a line for a refused tree act reads.
export type RunInFlightRefusal = {
  // The surface's line while the refusal still holds, null otherwise. For a
  // surface with its own in-flight signal, always null while runInFlight is
  // true: its hint (or its hidden act) says it then, so the line never shows
  // twice.
  line: string | null;
  // Offer a failed act's error: a `.run_in_flight` refusal is taken (true) -
  // the rule shows its line and asks the server about the run - and anything
  // else is left to the surface's own failure line (false).
  note: (err: unknown) => boolean;
  // Drop the refusal (the founder acts again).
  clear: () => void;
  // (d) below: true while the newest products answer, newer than the
  // standing in-flight refusal, names no active run for the product - the
  // server's lease, the authority, says the run that refusal met has ended. A
  // surface that refuses before posting (the covered-by amend) stands aside
  // then and lets the server answer.
  serverShowsNoRun: boolean;
};

type Refusal = {
  // The products list's dataUpdateCount when the refusal landed: an answer
  // with a higher count arrived after it.
  answersSeen: number;
  // Whether its line still holds. (b)'s newer products answer ends the line
  // but keeps the refusal, which (d) still reads.
  lineHolds: boolean;
};

// The one rule for a `.run_in_flight` refusal's line (0159 F1), shared by every
// surface that shows it: the title editor (EditableTitle, on the Product
// breadcrumb and the Dashboard row), the Review page's covered-by amend and
// the spec drawer's amend. The line never outlives the run:
//   (a) a refusal invalidates ["products"], so discovery (ActiveRunProvider)
//       reads active_run_id and revives or joins the run the tab did not know
//       about;
//   (b) the line shows only until the server's answer about THIS product's
//       run changes: it clears when runInFlight changes (the tab learned of
//       the run, or the run ended), and when a products answer newer than the
//       refusal names no active run for the product (the run ended between
//       the 409 and the refetch);
//   (c) while runInFlight is true the line is null (the surface's hint shows
//       F1's line once, never twice);
//   (d) the answer that ends the line in (b) - a products answer newer than
//       the refusal naming no active run for the product - is also
//       serverShowsNoRun, while it is the newest answer and the refusal
//       stands (a new refusal, clear(), or a runInFlight change that drops
//       the refusal in (b) ends it): the server's lease is the authority (its
//       check is atomic at commit), so a surface whose refusal before posting
//       read a stale snapshot (a store still reconnecting after the run
//       ended) stands aside, and its next act posts - the server accepts it
//       or answers its own 409, which lands here as a new refusal.
// A surface with no in-flight signal of its own (ownInFlightSignal false: the
// covered-by amend) is told only by its line, so for it (c) does not hold -
// the line shows while runInFlight is true - and in (b) only runInFlight
// turning false (the run ended) clears it: runInFlight turning true is the
// tab catching up with the run the refusal named, not a change in the
// server's answer, and clearing then would take the surface's only line away
// while that run is still going.
// Any other failure is the surface's own, as before (note returns false).
export function useRunInFlightRefusal(
  productId: string,
  runInFlight: boolean,
  { line = RUN_IN_FLIGHT_LINE, ownInFlightSignal = true }: RunInFlightRefusalOptions = {},
): RunInFlightRefusal {
  const queryClient = useQueryClient();
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [lastRunInFlight, setLastRunInFlight] = useState(runInFlight);

  // The products list's query state, re-read whenever the cache changes
  // (batched like TanStack's own observers, so no update lands mid-render).
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(notifyManager.batchCalls(onChange)),
    [queryClient],
  );
  const products = useSyncExternalStore(subscribe, () => queryClient.getQueryState(["products"]));

  // The one reading of "a products answer newer than the refusal names no
  // active run for the product": (b) ends the line on it, (d) returns it.
  const serverShowsNoRun =
    refusal !== null &&
    products !== undefined &&
    products.dataUpdateCount > refusal.answersSeen &&
    !namesActiveRun(products.data, productId);

  // (b), adjusted during render (React's pattern for state reset by a
  // changed input), so a stale line is never painted.
  if (lastRunInFlight !== runInFlight) {
    setLastRunInFlight(runInFlight);
    if (refusal !== null && (ownInFlightSignal || !runInFlight)) setRefusal(null);
  } else if (refusal !== null && refusal.lineHolds && serverShowsNoRun) {
    setRefusal({ ...refusal, lineHolds: false });
  }

  return {
    line: refusal !== null && refusal.lineHolds && (!runInFlight || !ownInFlightSignal) ? line : null,
    note: (err) => {
      if (!isRunInFlightRefusal(err)) return false;
      setRefusal({ answersSeen: queryClient.getQueryState(["products"])?.dataUpdateCount ?? 0, lineHolds: true });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      return true;
    },
    clear: () => setRefusal(null),
    serverShowsNoRun,
  };
}

// namesActiveRun: the products list answer names a live run lease on the
// product (its active_run_id).
function namesActiveRun(data: unknown, productId: string): boolean {
  const products = (data as { products?: { id?: unknown; active_run_id?: unknown }[] } | undefined)?.products;
  const product = products?.find((p) => p.id === productId);
  return typeof product?.active_run_id === "string" && product.active_run_id !== "";
}
